import { Box, Button, Grid, HStack, Stack, Tag, VStack } from '@chakra-ui/react';
import InputCopyField from 'components/form-elements/InputCopyField';
import { ServiceRecordIcon } from 'components/Icons';
import { dateTimeTransformer } from 'helpers/dateTransformer';
import { ContentCard } from 'layout/ContentCard';
import { get } from 'lodash';
import { SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdArrowDownward } from 'react-icons/md';
import { MdOpenInNew } from 'react-icons/md';
import { useCopyToClipboard } from 'usehooks-ts';

import { Account, Workshop, WorkshopVerificationRequest } from '../../api/types';
import { downloadFile } from '../../helpers/downloadHelper';
import useFileQuery from '../../hooks/useFileQuery';
import useWorkshopVerificationRequestQuery from '../../hooks/useWorkshopVerificationRequestQuery';
import AccountInputCopyFieldButtonsCard from './AccountInputCopyFieldButtonsCard';
import { CopyAccountDataAsJsonButton } from './CopyAccountDataAsJsonButton';

const AccountCard = ({ workshop, account, isLoading }: { workshop: Workshop | undefined; account: Account | undefined; isLoading: boolean }) => {
  const [value, copy] = useCopyToClipboard();
  const { t } = useTranslation();
  const [url, setUrl] = useState<SetStateAction<any>>(undefined);
  const [downloadDocument, setDownloadDocument] = useState<SetStateAction<any>>(undefined);
  const [click, setClick] = useState(false);
  const fileQuery = useFileQuery(url);

  const workshopVerification = workshop?.verifications;
  const latestApprovedVerificationId = workshopVerification
    ?.reduce((approvedVerificationsArray: WorkshopVerificationRequest[], verification: WorkshopVerificationRequest) => {
      if (verification?.status === 700) {
        approvedVerificationsArray.push(verification);
      }

      return approvedVerificationsArray;
    }, [])
    ?.at(0)?.id;
  const verificationDownloadDataObject = useWorkshopVerificationRequestQuery(latestApprovedVerificationId?.toString());
  const businessLicenceFileId = verificationDownloadDataObject?.data?.data?.business_licence?.id;
  const businessLicenceDownloadFile = verificationDownloadDataObject?.data?.data?.business_licence;
  const identificationFileId = verificationDownloadDataObject?.data?.data?.identification?.id;
  const identificationDownloadFile = verificationDownloadDataObject?.data?.data?.identification;
  const powerOfAttorneyFileId = verificationDownloadDataObject?.data?.data?.power_of_attorney?.id;
  const powerOfAttorneyDownloadFile = verificationDownloadDataObject?.data?.data?.power_of_attorney;
  const handwerkskarteFileId = verificationDownloadDataObject?.data?.data?.handwerkskarte?.id;
  const handwerkskarteDownloadFile = verificationDownloadDataObject?.data?.data?.handwerkskarte;
  useEffect(() => {
    downloadFile(downloadDocument, fileQuery.data);
    setClick(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileQuery.data, click]);

  return (
    <ContentCard
      isLoading={isLoading}
      icon={<ServiceRecordIcon boxSize={10} />}
      minH="fit-content"
      header={t('pages:account.details.title', {
        portalName: account?.portal?.name,
      })}
      headerAction={account?.type ? <Tag variant="solid">{t(`account:type.${account.type}.label`)}</Tag> : undefined}
    >
      <Grid w="full" display={'flex'} flexDir={'column'}>
        <Grid alignSelf={'flex-end'} display="flex" flexDir={'row'} gap="4">
          {workshop != null && account?.portal != null && (
            <CopyAccountDataAsJsonButton
              accountData={{
                ...workshop,
                username: account.username,
                password: account.password,
                countryName: t(`location:countries.${workshop.country}`, { lng: 'en' }),
                languageName: t(`location:languages.${workshop.language}`, { lng: 'en' }),
                vatWithoutPrefix: workshop.op_vat_number?.substring(workshop.op_vat_number.search(/\d/)) ?? '',
                vat_number: `${workshop.country}${workshop.op_vat_number}`,
                locale: `${workshop.language.toLowerCase()}_${workshop.country}`,
                customerIdWithoutPrefix: workshop.user_readable_id.split('-').at(-1) ?? '',
              }}
              manufacturer={account.portal.name}
            />
          )}
          {account?.portal?.registration_url ? (
            <Button
              rightIcon={<MdOpenInNew />}
              variant="primary"
              size="md"
              width={'fit-content'}
              disabled={account?.status === 200}
              onClick={() => {
                window.open(account?.portal?.registration_url, '_blank');
              }}
            >
              {t('account:register.label')}
            </Button>
          ) : null}
          {account?.portal?.url ? (
            <Button
              rightIcon={<MdOpenInNew />}
              variant="primary"
              size="md"
              width={'fit-content'}
              disabled={account?.status !== 200}
              onClick={() => {
                window.open(account?.portal?.url, '_blank');
              }}
            >
              {account?.portal?.name}
            </Button>
          ) : null}
        </Grid>
        <HStack>
          <Stack spacing={4} mt={'50px'} alignSelf={'flex-start'}>
            {workshop != null && account != null && <AccountInputCopyFieldButtonsCard workshop={workshop} account={account} />}
            <Stack>
              <InputCopyField
                copy={{
                  currentCopiedValue: value,
                  handleCopy: copy,
                }}
                field={{
                  value: dateTimeTransformer(get(workshop, 'registered_at')!),
                  label: t('workshop:registered_at.label'),
                }}
                aria-label={'registered_at'}
              />
            </Stack>

            <Stack>
              <InputCopyField
                copy={{
                  currentCopiedValue: value,
                  handleCopy: copy,
                }}
                field={{
                  value: dateTimeTransformer(get(workshop, 'activated_at')!),
                  label: t('workshop:activated_at.label'),
                }}
                aria-label={'activated_at'}
              />
            </Stack>
          </Stack>
          <Box alignSelf={'flex-start'} style={{ marginLeft: 'auto' }} pt={'12'} mr={0}>
            <VStack>
              <VStack alignItems={'flex-end'} spacing={3}>
                {(account?.open_task || account?.status === 150) && (
                  <>
                    <Button
                      minWidth={72}
                      leftIcon={<MdArrowDownward />}
                      variant="primary"
                      disabled={!latestApprovedVerificationId}
                      onClick={() => {
                        setUrl(`workshop-verification-request/file/${identificationFileId}/download`);
                        setDownloadDocument(identificationDownloadFile);
                        setClick(true);
                      }}
                    >
                      {t('workshop_verification_request:download_documents.identification')}
                    </Button>
                    <Button
                      minWidth={72}
                      leftIcon={<MdArrowDownward />}
                      variant="primary"
                      disabled={!latestApprovedVerificationId}
                      onClick={() => {
                        setUrl(`workshop-verification-request/file/${powerOfAttorneyFileId}/download`);
                        setDownloadDocument(powerOfAttorneyDownloadFile);
                        setClick(true);
                      }}
                    >
                      {t('workshop_verification_request:download_documents.power_of_attorney')}
                    </Button>
                    <Button
                      minWidth={72}
                      leftIcon={<MdArrowDownward />}
                      variant="primary"
                      disabled={!latestApprovedVerificationId}
                      onClick={() => {
                        setUrl(`workshop-verification-request/file/${businessLicenceFileId}/download`);
                        setDownloadDocument(businessLicenceDownloadFile);
                        setClick(true);
                      }}
                    >
                      {t('workshop_verification_request:download_documents.business_licence')}
                    </Button>
                  </>
                )}

                {handwerkskarteFileId &&
                  workshop?.country?.toLocaleUpperCase() === 'DE' &&
                  account?.portal?.name?.toLocaleLowerCase() === 'mazda' && (
                    <Button
                      minWidth={72}
                      leftIcon={<MdArrowDownward />}
                      variant="primary"
                      disabled={!latestApprovedVerificationId}
                      onClick={() => {
                        setUrl(`workshop-verification-request/file/${handwerkskarteFileId}/download`);
                        setDownloadDocument(handwerkskarteDownloadFile);
                        setClick(true);
                      }}
                    >
                      {t('workshop_verification_request:download_documents.handwerkskarte')}
                    </Button>
                  )}
              </VStack>
            </VStack>
          </Box>
        </HStack>
      </Grid>
    </ContentCard>
  );
};

export default AccountCard;
